/**
 * PATH: apps/qa/src/components/inspection/FloorBuilder/index.tsx
 * PURPOSE: Enhanced visual floor builder with AI suggestions
 *
 * WHY: Transform basic floor input into an intelligent, visual, AI-assisted
 * building structure builder that makes creating inspection scopes intuitive.
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ AI Analyzes │───▶│ User Adjusts │───▶│ Generate    │
 *   │ Job Context │    │ Visual UI    │    │ Structure   │
 *   └─────────────┘    └──────────────┘    └─────────────┘
 *
 * DEPENDENCIES:
 *   - ./AIAnalysisCard: AI suggestion card
 *   - ./VisualPreview: Building diagram preview
 *   - ./constants: Floor type styles and helpers
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Building2, Sparkles, Plus, Minus, Check, X, ArrowUp, ArrowDown, Lightbulb } from 'lucide-react'
import type { FloorBuilderProps, FloorConfig, AISuggestions } from './types'
import { generateId, generateAreaCode } from './constants'
import { AIAnalysisCard } from './AIAnalysisCard'
import { VisualPreview } from './VisualPreview'
import {
  analyzeForFloorBuilder,
  estimateInspectionTime,
  AVAILABLE_TYPICAL_AREAS,
  AVAILABLE_BASEMENT_AREAS,
  AVAILABLE_ROOFTOP_AREAS,
} from '@/lib/floorBuilderAI'

export function FloorBuilder({
  job, orgPrimaryTrade = 'Waterproofing', buildingClasses = [], tradeTags = [],
  category = 'commercial', projectType = 'new_construction', onGenerate, onCancel, initialFloors,
}: FloorBuilderProps) {
  const [floorCount, setFloorCount] = useState(1)
  const [includeBasement, setIncludeBasement] = useState(false)
  const [includeRooftop, setIncludeRooftop] = useState(false)
  const [typicalAreas, setTypicalAreas] = useState<string[]>([])
  const [customArea, setCustomArea] = useState('')
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestions | null>(null)
  const [aiApplied, setAiApplied] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [floorInputValue, setFloorInputValue] = useState('1')

  // AI Analysis on Mount
  useEffect(() => {
    if (job || tradeTags.length > 0 || buildingClasses.length > 0) {
      setIsAnalyzing(true)
      const timer = setTimeout(() => {
        const suggestions = analyzeForFloorBuilder({
          jobId: job?.job_id, jobName: job?.site || job?.project_name || '',
          clientName: job?.client || '', buildingClasses, projectType, category, tradeTags, orgPrimaryTrade,
        })
        setAiSuggestions(suggestions)
        setIsAnalyzing(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [job, tradeTags, buildingClasses, category, projectType, orgPrimaryTrade])

  // Generate Floor Configurations
  const generatedFloors = useMemo((): FloorConfig[] => {
    const floors: FloorConfig[] = []
    let sortOrder = 0

    if (includeBasement) {
      const basementAreas = aiSuggestions?.basementAreas || AVAILABLE_BASEMENT_AREAS.slice(0, 3)
      floors.push({
        id: `basement-${generateId()}`, name: 'Basement', type: 'basement', isCustomized: false, sortOrder: sortOrder++,
        areas: basementAreas.map(name => ({ id: generateId(), name, code: generateAreaCode('Basement', name), aiSuggested: aiSuggestions?.basementAreas?.includes(name) })),
      })
    }

    const groundAreas = aiSuggestions?.groundAreas || ['Lobby', 'Entry', ...typicalAreas.slice(0, 2)]
    floors.push({
      id: `ground-${generateId()}`, name: 'Ground Floor', type: 'ground', isCustomized: false, sortOrder: sortOrder++,
      areas: [...new Set(groundAreas)].map(name => ({ id: generateId(), name, code: generateAreaCode('Ground', name), aiSuggested: aiSuggestions?.groundAreas?.includes(name) || aiSuggestions?.typicalAreas?.includes(name) })),
    })

    for (let i = 1; i < floorCount; i++) {
      const floorName = `Level ${i}`
      floors.push({
        id: `level-${i}-${generateId()}`, name: floorName, type: 'standard', isCustomized: false, sortOrder: sortOrder++,
        areas: typicalAreas.map(name => ({ id: generateId(), name, code: generateAreaCode(floorName, name), aiSuggested: aiSuggestions?.typicalAreas?.includes(name) })),
      })
    }

    if (includeRooftop) {
      const rooftopAreas = aiSuggestions?.rooftopAreas || AVAILABLE_ROOFTOP_AREAS.slice(0, 3)
      floors.push({
        id: `rooftop-${generateId()}`, name: 'Rooftop', type: 'rooftop', isCustomized: false, sortOrder: sortOrder++,
        areas: rooftopAreas.map(name => ({ id: generateId(), name, code: generateAreaCode('Rooftop', name), aiSuggested: aiSuggestions?.rooftopAreas?.includes(name) })),
      })
    }
    return floors
  }, [floorCount, includeBasement, includeRooftop, typicalAreas, aiSuggestions])

  const stats = useMemo(() => ({
    totalFloors: generatedFloors.length,
    totalAreas: generatedFloors.reduce((sum, f) => sum + f.areas.length, 0),
    timeEstimate: estimateInspectionTime(floorCount, typicalAreas.length || 3, includeBasement, includeRooftop),
  }), [generatedFloors, floorCount, typicalAreas, includeBasement, includeRooftop])

  // Handlers
  const handleFloorCountChange = useCallback((value: string) => {
    setFloorInputValue(value)
    if (value === '') return
    const num = parseInt(value, 10)
    if (!isNaN(num) && num >= 1 && num <= 99) setFloorCount(num)
  }, [])

  const handleFloorCountBlur = useCallback(() => {
    const num = parseInt(floorInputValue, 10)
    if (isNaN(num) || num < 1) { setFloorCount(1); setFloorInputValue('1') }
    else if (num > 99) { setFloorCount(99); setFloorInputValue('99') }
    else { setFloorCount(num); setFloorInputValue(num.toString()) }
  }, [floorInputValue])

  const incrementFloors = () => { if (floorCount < 99) { setFloorCount(p => p + 1); setFloorInputValue((floorCount + 1).toString()) } }
  const decrementFloors = () => { if (floorCount > 1) { setFloorCount(p => p - 1); setFloorInputValue((floorCount - 1).toString()) } }
  const toggleTypicalArea = (area: string) => setTypicalAreas(p => p.includes(area) ? p.filter(a => a !== area) : [...p, area])
  const addCustomArea = () => { if (customArea.trim() && !typicalAreas.includes(customArea.trim())) { setTypicalAreas(p => [...p, customArea.trim()]); setCustomArea('') } }

  const applyAISuggestions = () => {
    if (!aiSuggestions) return
    setFloorCount(aiSuggestions.floorCount); setFloorInputValue(aiSuggestions.floorCount.toString())
    setIncludeBasement(aiSuggestions.includeBasement); setIncludeRooftop(aiSuggestions.includeRooftop)
    setTypicalAreas(aiSuggestions.typicalAreas); setAiApplied(true)
  }

  useEffect(() => { setFloorInputValue(floorCount.toString()) }, [floorCount])

  return (
    <div className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent overflow-hidden animate-in slide-in-from-top-2 duration-300">
      <div className="flex items-center justify-between p-4 border-b border-primary/20 bg-primary/5">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">Quick Structure Builder</h3>
        </div>
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Controls Panel */}
        <div className="p-4 space-y-4 border-b lg:border-b-0 lg:border-r border-primary/20">
          <AIAnalysisCard isAnalyzing={isAnalyzing} aiSuggestions={aiSuggestions} aiApplied={aiApplied} onApply={applyAISuggestions} />

          {/* Floor Count */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Number of Floors</label>
            <div className="flex items-center gap-2">
              <button onClick={decrementFloors} disabled={floorCount <= 1} className="w-10 h-10 rounded-lg border border-input bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"><Minus className="w-4 h-4" /></button>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={floorInputValue} onChange={(e) => handleFloorCountChange(e.target.value)} onBlur={handleFloorCountBlur} className="w-16 h-10 text-center text-lg font-semibold rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
              <button onClick={incrementFloors} disabled={floorCount >= 99} className="w-10 h-10 rounded-lg border border-input bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"><Plus className="w-4 h-4" /></button>
              <span className="text-sm text-muted-foreground ml-2">Ground + {floorCount - 1} upper level{floorCount > 2 ? 's' : ''}</span>
            </div>
          </div>

          {/* Special Floors */}
          <div className="grid grid-cols-2 gap-3">
            <label className={cn('flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all', includeBasement ? 'border-blue-500/50 bg-blue-500/10' : 'border-border hover:border-blue-500/30 hover:bg-blue-500/5')}>
              <input type="checkbox" checked={includeBasement} onChange={(e) => setIncludeBasement(e.target.checked)} className="w-4 h-4 rounded border-input accent-blue-500" />
              <div className="flex-1 min-w-0"><div className="flex items-center gap-1.5"><ArrowDown className="w-3.5 h-3.5 text-blue-500" /><span className="font-medium text-sm text-foreground">Basement</span></div><p className="text-xs text-muted-foreground truncate">Carpark, plant, storage</p></div>
            </label>
            <label className={cn('flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all', includeRooftop ? 'border-amber-500/50 bg-amber-500/10' : 'border-border hover:border-amber-500/30 hover:bg-amber-500/5')}>
              <input type="checkbox" checked={includeRooftop} onChange={(e) => setIncludeRooftop(e.target.checked)} className="w-4 h-4 rounded border-input accent-amber-500" />
              <div className="flex-1 min-w-0"><div className="flex items-center gap-1.5"><ArrowUp className="w-3.5 h-3.5 text-amber-500" /><span className="font-medium text-sm text-foreground">Rooftop</span></div><p className="text-xs text-muted-foreground truncate">Plant, HVAC, access</p></div>
            </label>
          </div>

          {/* Area Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Areas per Floor</label>
              <span className="text-xs text-muted-foreground">{typicalAreas.length} selected</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {AVAILABLE_TYPICAL_AREAS.slice(0, 12).map(area => {
                const isSelected = typicalAreas.includes(area)
                const isAISuggested = aiSuggestions?.typicalAreas?.includes(area)
                return (
                  <button key={area} onClick={() => toggleTypicalArea(area)} className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all', isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground')}>
                    {isAISuggested && !isSelected && <Sparkles className="w-3 h-3 text-primary" />}{area}{isSelected && <Check className="w-3 h-3" />}
                  </button>
                )
              })}
            </div>
            <div className="flex gap-2">
              <input type="text" value={customArea} onChange={(e) => setCustomArea(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCustomArea()} placeholder="Add custom area..." className="flex-1 px-3 py-1.5 rounded-lg border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all" />
              <button onClick={addCustomArea} disabled={!customArea.trim()} className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><Plus className="w-4 h-4" /></button>
            </div>
            {aiSuggestions && aiSuggestions.typicalAreas.length > 0 && !aiApplied && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-primary" />AI suggests: {aiSuggestions.typicalAreas.slice(0, 4).join(', ')}{aiSuggestions.typicalAreas.length > 4 && ` +${aiSuggestions.typicalAreas.length - 4} more`}
              </p>
            )}
          </div>
        </div>

        {/* Visual Preview */}
        <VisualPreview generatedFloors={generatedFloors} stats={stats} />
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between gap-3 p-4 border-t border-primary/20 bg-muted/30">
        <p className="text-xs text-muted-foreground hidden sm:block"><Lightbulb className="w-3 h-3 inline mr-1" />Click floors in preview to view areas</p>
        <div className="flex gap-2 ml-auto">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-input text-sm font-medium hover:bg-accent active:scale-[0.98] transition-all">Cancel</button>
          <button onClick={() => onGenerate(generatedFloors)} disabled={generatedFloors.length === 0} className="px-6 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all">Generate Structure</button>
        </div>
      </div>
    </div>
  )
}

export default FloorBuilder
